"use client"

import React from "react"
import {
    motion,
    useTransform,
    type MotionValue,
    type Variants,
} from "framer-motion"
import { cn } from "@/lib/utils"
import { Card } from "@/components/ui/card"

interface TimelineItemEvent {
    id?: string
    year: string
    title: string
    subtitle?: string
    description: { text: string; completed: boolean }[]
    icon?: React.ReactNode
    color?: string
}

interface TimelineItemProps {
    event: TimelineItemEvent
    index: number
    activeIndex: number
    containerClassName: string
    cardClassName: string
    cardVariants: Variants
    parallaxIntensity: number
    smoothProgress: MotionValue<number>
    registerRef: (el: HTMLDivElement | null) => void
}

export const TimelineItem: React.FC<TimelineItemProps> = ({
    event,
    index,
    activeIndex,
    containerClassName,
    cardClassName,
    cardVariants,
    parallaxIntensity,
    smoothProgress,
    registerRef,
}) => {
    const yOffset = useTransform(
        smoothProgress,
        [0, 1],
        [parallaxIntensity * 100, -parallaxIntensity * 100],
    )

    return (
        <div ref={registerRef} className={containerClassName}>
            <div
                className={cn(
                    "absolute top-1/2 left-1/2 z-30 -translate-x-1/2 -translate-y-1/2 transform",
                )}
            >
                <motion.div
                    className={cn(
                        "bg-background flex h-6 w-6 items-center justify-center rounded-full border-4",
                        index <= activeIndex
                            ? "border-primary"
                            : "border bg-card",
                    )}
                    animate={
                        index <= activeIndex
                            ? {
                                  scale: [1, 1.3, 1],
                                  boxShadow: [
                                      "0 0 0px oklch(from var(--primary-glow-2) l c h / 0%)",
                                      "0 0 12px oklch(from var(--primary-glow-2) l c h / 0.6)",
                                      "0 0 0px oklch(from var(--primary-glow-2) l c h / 0%)",
                                  ],
                              }
                            : {}
                    }
                    transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        repeatDelay: 4,
                        ease: "easeInOut",
                    }}
                />
            </div>
            <motion.div
                className={cardClassName}
                variants={cardVariants}
                initial="initial"
                whileInView="whileInView"
                viewport={{ once: false, margin: "-100px" }}
                style={parallaxIntensity > 0 ? { y: yOffset } : undefined}
            >
                <Card className="border-primary/50 bg-primary/10 relative overflow-hidden border-2 p-6 pl-16">
                    <div className="text-primary/20 pointer-events-none absolute right-8 bottom-4 text-[150px] font-bold select-none">
                        {index + 1}
                    </div>
                    <h2 className="text-primary relative z-10 mb-4 text-base font-bold">
                        {event.year} - {event.title}
                    </h2>
                    <ul className="relative z-10 space-y-3">
                        {event.description.map((item, itemIndex) => (
                            <li
                                key={itemIndex}
                                className="flex items-start space-x-2"
                            >
                                <span className="text-primary/75 h-5 w-5 rounded-full text-base">
                                    {item.completed ? "☑" : "☐"}
                                </span>
                                <div>
                                    <p className="text-primary flex items-center gap-2 text-base leading-tight">
                                        {item.text}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ul>
                </Card>
            </motion.div>
        </div>
    )
}
